import { useEffect, useRef, useState } from 'react'
import { useViewLink } from '../lib/hooks'
import ConfirmModal from './ui/confirm-modal'
import type { ViewLinkResponse } from '../../../../types'

interface ViewLinkModalProps {
  albumUuid: string
  albumName: string
  onClose: () => void
}

const EXPIRY_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: 'never', label: 'Never' },
]

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Which option a Replace should start from: whatever the owner picked for the
 * link currently in force. Without this, an owner who chose "Never" and later
 * hits Replace silently gets the 30-day default back — and rotation cannot be
 * undone. The API stores absolute timestamps, not the chosen duration, so the
 * duration is recovered from createdAt→expiresAt and snapped to the nearest
 * offered option.
 */
function expiryOptionFor(link: ViewLinkResponse): string {
  if (!link.expiresAt) return 'never'
  const days = (new Date(link.expiresAt).getTime() - new Date(link.createdAt).getTime()) / DAY_MS
  if (!Number.isFinite(days)) return '30'
  return EXPIRY_OPTIONS.filter((o) => o.value !== 'never').reduce((best, option) =>
    Math.abs(Number(option.value) - days) < Math.abs(Number(best.value) - days) ? option : best,
  ).value
}

function isExpired(link: ViewLinkResponse): boolean {
  return Boolean(link.expiresAt) && new Date(link.expiresAt!).getTime() <= Date.now()
}

/**
 * "20 July", or "20 July 2026" when the date is not in the current year.
 *
 * The locale is pinned to en-GB rather than the runtime's: the default format
 * renders 7/20/2026 under en-US and 20/7/2026 nearly everywhere else, and an
 * owner deciding whether a share link is still open should not have to work
 * out which of two numbers is the month.
 */
function formatDay(iso: string): string {
  const d = new Date(iso)
  const thisYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    ...(thisYear ? {} : { year: 'numeric' }),
  })
}

export default function ViewLinkModal({ albumUuid, albumName, onClose }: ViewLinkModalProps) {
  const { link, loading, error, loadFailed, refresh, create, revoke } = useViewLink(albumUuid)
  const [expiry, setExpiry] = useState('30')
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)
  const [confirming, setConfirming] = useState<'replace' | 'revoke' | null>(null)
  const seededFor = useRef<string | null>(null)

  // Re-seed once per distinct link. Doing it on every render would fight the
  // owner's own selection; keying on the token also resets `copied` when a
  // replacement mints a URL nobody has copied yet.
  useEffect(() => {
    if (!link || seededFor.current === link.token) return
    seededFor.current = link.token
    setExpiry(expiryOptionFor(link))
    setCopied(false)
  }, [link])

  const expired = link ? isExpired(link) : false
  const shareUrl = link ? `${window.location.origin}/view/${link.token}` : ''

  const handleCreate = async () => {
    setBusy(true)
    try {
      await create(expiry === 'never' ? null : Number(expiry))
    } catch {
      // error surfaces through the hook's error state
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  const handleRevoke = async () => {
    setBusy(true)
    try {
      await revoke()
    } catch {
      // error surfaces through the hook's error state
    } finally {
      setBusy(false)
      setConfirming(null)
    }
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(shareUrl)
    setCopied(true)
  }

  // An expired link is dead for every recipient, so it is offered as a fresh
  // creation, not as a copy/replace of something that still works.
  const showLive = !loading && !loadFailed && link !== null && !expired
  const showExpired = !loading && !loadFailed && link !== null && expired
  const showCreate = !loading && !loadFailed && link === null

  const expirySelect = (labelText: string) => (
    <div className="form-group">
      <label htmlFor="view-link-expiry">{labelText}</label>
      <select id="view-link-expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)}>
        {EXPIRY_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  )

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        {/* `modal-content`, not `modal` — matches the existing share modal in
            album-list.tsx:1249-1250 and the classes defined in modal.css. */}
        <div className="modal-content view-link-modal" onClick={(e) => e.stopPropagation()}>
          <h3>A link to &ldquo;{albumName}&rdquo;</h3>
          <p>
            Anyone with this link can look through these memories and keep copies of
            them. No account needed.
          </p>

          {loading && <p>Loading…</p>}

          {/* A failed GET is not the same as "no link exists": showing the create
              form here would let one click rotate — and so destroy — a live URL
              the owner was never shown. */}
          {!loading && loadFailed && (
            <p>We couldn&rsquo;t check this album&rsquo;s link just now.</p>
          )}

          {showCreate && expirySelect('Expires')}

          {showLive && (
            <>
              {/* A textarea, not an input: a real token is 32 random bytes
                  base64url, so the URL runs ~66 characters and an input of any
                  width in a 400px modal shows the owner half of what they are
                  about to share. It still carries a value, so it stays
                  selectable and findable by display value. */}
              <div className="form-group view-link-modal__url">
                <textarea readOnly rows={2} value={shareUrl} aria-label="Share URL" />
                <button className="secondary-button" onClick={handleCopy}>
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p>
                Shared {formatDay(link!.createdAt)}
                {link!.expiresAt
                  ? ` · open until ${formatDay(link!.expiresAt)}`
                  : ' · open until you close it'}
              </p>
              {expirySelect('New link expires')}
              <p className="warning">
                Replacing this link makes the current URL stop working immediately, and
                revoking it does the same. Neither can be undone.
              </p>
            </>
          )}

          {/* An action that failed still has to say so — this covers a failed
              create or revoke, not the initial load, which has its own message
              and Retry above. No "Error:" prefix: the string from
              getErrorMessage is already written for a person, and labelling it
              as an error twice is developer output leaking into the UI. */}
          {error && !loadFailed && <p className="error-message">{error}</p>}

          {showExpired && (
            <>
              <p className="warning">
                This link closed on {formatDay(link!.expiresAt!)} and no longer opens for
                anyone. Create a new one to share these memories again.
              </p>
              {expirySelect('New link expires')}
            </>
          )}

          {/* Single bottom action row, matching the existing share modal
              (album-list.tsx:1253-1262) — modal.css styles `.modal-actions`
              and `.primary-button`, not bare sibling buttons. */}
          <div className="modal-actions">
            {!loading && loadFailed && (
              <button className="primary-button" onClick={() => { void refresh() }}>
                Retry
              </button>
            )}
            {showCreate && (
              <button className="primary-button" onClick={handleCreate} disabled={busy}>
                {busy ? 'Creating…' : 'Create link'}
              </button>
            )}
            {showExpired && (
              <button className="primary-button" onClick={handleCreate} disabled={busy}>
                {busy ? 'Creating…' : 'Create a new link'}
              </button>
            )}
            {showLive && (
              <>
                {/* Deliberately not `primary-button`: both of these are
                    irreversible, and neither should be the control the eye
                    lands on first. */}
                <button
                  className="secondary-button"
                  onClick={() => setConfirming('replace')}
                  disabled={busy}
                >
                  Replace link
                </button>
                <button
                  className="secondary-button"
                  onClick={() => setConfirming('revoke')}
                  disabled={busy}
                >
                  Revoke
                </button>
              </>
            )}
            <button className="secondary-button" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>

      {/* Rendered outside the overlay above so a click inside the confirmation
          doesn't reach that overlay's onClose. */}
      <ConfirmModal
        isOpen={confirming === 'replace'}
        variant="warning"
        title="Replace this view link?"
        message={
          'The current URL stops working immediately for everyone you have already shared it ' +
          'with, and this cannot be undone. A new URL is created in its place.'
        }
        confirmText="Yes, replace"
        onConfirm={handleCreate}
        onCancel={() => setConfirming(null)}
        isLoading={busy}
      />
      <ConfirmModal
        isOpen={confirming === 'revoke'}
        variant="danger"
        title="Revoke this view link?"
        message={
          'The URL stops working immediately for everyone you have already shared it with, ' +
          'and this cannot be undone. Nobody will be able to view this album until you create ' +
          'a new link.'
        }
        confirmText="Yes, revoke"
        onConfirm={handleRevoke}
        onCancel={() => setConfirming(null)}
        isLoading={busy}
      />
    </>
  )
}
